import { heapUserInit } from "@newsela/snitch";
import { isEmpty, isNil, isUndefined } from "lodash-es";
import { useEffect } from "react";

import { VIEW_PAGE_LUNA_AI_DESIGN } from "@/ai/constants/experiments";
import getClassroomSubjectsList from "@/classrooms/utils/getClassroomSubjectsList";
import useGetUserExperimentSegment from "@/features/useGetUserExperimentSegment";
import useUser from "@/users/useUser";
import useUserClassroomGet from "@/users/useUserClassroomGet";
import { SHOW_LESSON_PLAN_ASSESSMENT_QUESTIONS } from "@/view/ContentDisplay/TeacherTools/LessonPlannerTool/LessonPlannerTool.constants";
import { VIEW_PAGE_TRANSLATOR_FEATURE } from "@/view/SecondaryActions/TranslateAction/constants.ts";
import { EXPERIMENT_TO_RETRIEVE_CONTENT_FROM_V2_LANGSTON_API } from "@/search/Langston/constants";

const useAddCustomHeapAttributes = () => {
  const [gradingRecommendationsVariation] = useGetUserExperimentSegment(
    "reading_skills_recommendations_experiment_test",
  );
  const [elaSkillsGradingLink] = useGetUserExperimentSegment(
    "ela_skills_grading_link",
  );
  const [isFormativeCuratedConnection] = useGetUserExperimentSegment(
    "formative_curated_connection",
  );

  const [isFormativeSectionExpanded] = useGetUserExperimentSegment(
    "formative_section_expanded",
  );

  const [recentlyViewCache] = useGetUserExperimentSegment(
    "recently_view_cache",
  );
  const [referralCtaTextExperiment] =
    useGetUserExperimentSegment("referral_cta_text");

  const [viewPageTranslatorVariation] = useGetUserExperimentSegment(
    VIEW_PAGE_TRANSLATOR_FEATURE,
  );

  const [viewPageLunaAIVariation] = useGetUserExperimentSegment(
    VIEW_PAGE_LUNA_AI_DESIGN,
  );

  const [readAloudQuizzesVariation] =
    useGetUserExperimentSegment("read_aloud_quizzes");

  const [newTeacherHomepageExperiment] = useGetUserExperimentSegment(
    "new_teacher_homepage",
  );
  const [showLockIconsExperiment] =
    useGetUserExperimentSegment("show_lock_icons");
  const [formativeMatchingGame] = useGetUserExperimentSegment(
    "formative_matching_game",
  );
  const [useOAuthFormativeExperiment] = useGetUserExperimentSegment(
    "use-oauth-formative",
  );
  const [userTrialActivation] =
    useGetUserExperimentSegment("reverse_trial_2024");

  const [printModalVariation] = useGetUserExperimentSegment("print_modal");

  const [langstonSearchExperiment] = useGetUserExperimentSegment(
    EXPERIMENT_TO_RETRIEVE_CONTENT_FROM_V2_LANGSTON_API,
  );

  const [aiSmartSearchExperiment] =
    useGetUserExperimentSegment("ai_assisted_search");

  const [langstonSuggestionsExperiment] = useGetUserExperimentSegment(
    "langston_suggestions",
  );

  const [showAssessmentQuestions] = useGetUserExperimentSegment(
    SHOW_LESSON_PLAN_ASSESSMENT_QUESTIONS,
  );

  const [experimentList] = useGetUserExperimentSegment();
  const { data: user } = useUser();
  const { data: { results: classrooms = [] } = {}, loading: loadingClasses } =
    useUserClassroomGet(user?.id, { hide_archived: true });

  useEffect(() => {
    if (!isNil(user?.teacher) && !loadingClasses) {
      const classroomSubjects = getClassroomSubjectsList(classrooms);
      const classroomSubjectList = classroomSubjects.length
        ? classroomSubjects.toString()
        : "N/A";
      heapUserInit(classroomSubjectList, "classroom_subjects");
    }
  }, [loadingClasses]);

  useEffect(() => {
    if (!isNil(user?.teacher) || !isNil(user?.student)) {
      let activeExperimentNames = "N/A";
      if (!isUndefined(experimentList) && !isEmpty(experimentList)) {
        activeExperimentNames = Object.keys(experimentList).toString();
      }
      heapUserInit(activeExperimentNames, "experiment_group");
    }
  }, [experimentList]);

  useEffect(() => {
    if (formativeMatchingGame !== null) {
      heapUserInit(formativeMatchingGame, "formative_matching_game");
    }
  }, [formativeMatchingGame]);

  useEffect(() => {
    if (userTrialActivation !== null) {
      heapUserInit(userTrialActivation, "reverse_trial_2024");
    }
  }, [userTrialActivation]);

  useEffect(() => {
    if (gradingRecommendationsVariation !== null) {
      heapUserInit(
        gradingRecommendationsVariation,
        "reading_skills_recommendations_experiment_test",
      );
    }
  }, [gradingRecommendationsVariation]);

  useEffect(() => {
    if (elaSkillsGradingLink !== null) {
      heapUserInit(elaSkillsGradingLink, "ela_skills_grading_link");
    }
  }, [elaSkillsGradingLink]);

  useEffect(() => {
    if (isFormativeCuratedConnection !== null) {
      heapUserInit(
        isFormativeCuratedConnection,
        "formative_curated_connection",
      );
    }
  }, [isFormativeCuratedConnection]);

  useEffect(() => {
    if (isFormativeSectionExpanded !== null) {
      heapUserInit(isFormativeSectionExpanded, "formative_section_expanded");
    }
  }, [isFormativeSectionExpanded]);

  useEffect(() => {
    if (recentlyViewCache !== null) {
      heapUserInit(recentlyViewCache, "recently_view_cache");
    }
  }, [recentlyViewCache]);

  useEffect(() => {
    if (referralCtaTextExperiment !== null) {
      heapUserInit(referralCtaTextExperiment, "referral_cta_text");
    }
  }, [referralCtaTextExperiment]);

  useEffect(() => {
    if (newTeacherHomepageExperiment !== null) {
      heapUserInit(newTeacherHomepageExperiment, "new_teacher_homepage");
    }
  }, [newTeacherHomepageExperiment]);

  useEffect(() => {
    if (showLockIconsExperiment !== null) {
      heapUserInit(showLockIconsExperiment, "show_lock_icons");
    }
  }, [showLockIconsExperiment]);

  useEffect(() => {
    if (useOAuthFormativeExperiment !== null) {
      heapUserInit(useOAuthFormativeExperiment, "use-oauth-formative");
    }
  }, [useOAuthFormativeExperiment]);

  useEffect(() => {
    if (printModalVariation !== null) {
      heapUserInit(printModalVariation, "print_modal");
    }
  }, [printModalVariation]);

  useEffect(() => {
    if (readAloudQuizzesVariation !== null) {
      heapUserInit(readAloudQuizzesVariation, "read_aloud_quizzes");
    }
  }, [readAloudQuizzesVariation]);

  useEffect(() => {
    if (viewPageTranslatorVariation !== null) {
      heapUserInit(viewPageTranslatorVariation, VIEW_PAGE_TRANSLATOR_FEATURE);
    }
  }, [viewPageTranslatorVariation]);

  useEffect(() => {
    if (viewPageLunaAIVariation !== null) {
      heapUserInit(viewPageLunaAIVariation, VIEW_PAGE_LUNA_AI_DESIGN);
    }
  }, [viewPageLunaAIVariation]);

  useEffect(() => {
    if (langstonSearchExperiment !== null) {
      heapUserInit(
        langstonSearchExperiment,
        EXPERIMENT_TO_RETRIEVE_CONTENT_FROM_V2_LANGSTON_API,
      );
    }
  }, [langstonSearchExperiment]);

  useEffect(() => {
    if (aiSmartSearchExperiment !== null) {
      heapUserInit(aiSmartSearchExperiment, "ai_assisted_search");
    }
  }, [aiSmartSearchExperiment]);

  useEffect(() => {
    if (langstonSuggestionsExperiment !== null) {
      heapUserInit(langstonSuggestionsExperiment, "langston_suggestions");
    }
  }, [langstonSuggestionsExperiment]);

  useEffect(() => {
    if (showAssessmentQuestions !== null) {
      heapUserInit(
        showAssessmentQuestions,
        SHOW_LESSON_PLAN_ASSESSMENT_QUESTIONS,
      );
    }
  }, [showAssessmentQuestions]);
};

export default useAddCustomHeapAttributes;
